package com.arena_web.ui.components.notifications

import androidx.compose.runtime.*
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.sections.header.NotificationActorCard
import com.arena_web.ui.sections.header.extractInitials
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.toRgbString
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import kotlin.js.Date

val SnackbarSlideDownKeyframes = Keyframes {
    from { Modifier.translateY((-130).percent).opacity(0) }
    to { Modifier.translateY(0.percent).opacity(1) }
}

val SnackbarSlideUpKeyframes = Keyframes {
    from { Modifier.translateY(0.percent).opacity(1) }
    to { Modifier.translateY((-130).percent).opacity(0) }
}

data class SnackbarNotification(
    val id: Double = Date.now(),
    val title: String,
    val body: String? = null,
    /** Actor visuals — rendered as the same player-card avatar the
     *  notifications panel uses. */
    val avatarUrl: String? = null,
    val cardAssetsBasePath: String? = null,
    /** Icon shown instead of initials when there is no actor (e.g. rewards). */
    val fallbackIcon: String? = null,
)

/**
 * Single-slot banner state: a new notification replaces the one on screen
 * (the `key(data.id)` in the host restarts the enter animation and the
 * auto-dismiss timer).
 */
class NotificationSnackbarState {
    var current by mutableStateOf<SnackbarNotification?>(null)
        private set
    var closing by mutableStateOf(false)
        private set

    fun show(
        title: String,
        body: String? = null,
        avatarUrl: String? = null,
        cardAssetsBasePath: String? = null,
        fallbackIcon: String? = null,
    ) {
        current = SnackbarNotification(
            title = title,
            body = body,
            avatarUrl = avatarUrl,
            cardAssetsBasePath = cardAssetsBasePath,
            fallbackIcon = fallbackIcon,
        )
        closing = false
    }

    fun startClose() {
        if (current != null) closing = true
    }

    fun finishClose() {
        current = null
        closing = false
    }
}

private const val AUTO_DISMISS_MS = 4000L
private const val EXIT_ANIMATION_MS = 250

/**
 * Real-time notification banner — slides down from the top edge when a
 * `NEW_NOTIFICATION` WS event arrives, auto-dismisses after [AUTO_DISMISS_MS]
 * (tap dismisses immediately). Mounted once in `AppEntry`.
 */
@Composable
fun NotificationSnackbarHost(state: NotificationSnackbarState) {
    val data = state.current ?: return
    val mobile = isMobile()
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    key(data.id) {
        LaunchedEffect(Unit) {
            delay(AUTO_DISMISS_MS)
            state.startClose()
        }
        LaunchedEffect(state.closing) {
            if (state.closing) {
                delay(EXIT_ANIMATION_MS.toLong())
                state.finishClose()
            }
        }

        Box(
            modifier = Modifier
                .position(Position.Fixed)
                .top(0.px)
                .left(0.px)
                .right(0.px)
                .zIndex(2100)
                .pointerEvents(PointerEvents.None)
                .styleModifier {
                    property("padding", "12px 12px 0")
                    if (mobile) property("--scale", "var(--mobile-scale)")
                },
            contentAlignment = Alignment.TopCenter,
        ) {
            GradientBorderBox(
                modifier = Modifier
                    .pointerEvents(PointerEvents.Auto)
                    .cursor(Cursor.Pointer)
                    .onClick { state.startClose() }
                    .animation(
                        if (state.closing) {
                            SnackbarSlideUpKeyframes.toAnimation(
                                duration = EXIT_ANIMATION_MS.ms,
                                timingFunction = AnimationTimingFunction.EaseIn,
                                fillMode = AnimationFillMode.Forwards,
                            )
                        } else {
                            SnackbarSlideDownKeyframes.toAnimation(
                                duration = Constants.ANIMATION_NORMAL.ms,
                                timingFunction = AnimationTimingFunction.EaseOut,
                            )
                        }
                    )
                    .styleModifier {
                        property("width", "100%")
                        property("max-width", s(400))
                        property("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.35)")
                    },
                borderRadius = 16,
                background = colors.surface.toRgbString(),
                borderGradient = AppGradients.BorderGradient01,
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPadding(12, 14)
                        .sGap(10),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    // Same player-card avatar as the notifications panel —
                    // card frame + avatar when known, initials otherwise.
                    NotificationActorCard(
                        initials = if (data.fallbackIcon != null) "" else extractInitials(data.title),
                        fallbackIcon = data.fallbackIcon,
                        avatarUrl = data.avatarUrl,
                        cardAssetsBasePath = data.cardAssetsBasePath,
                    )

                    // Single inline paragraph — bold title, then the body right
                    // after it on the same line, clamped to two lines total.
                    Div(
                        attrs = Modifier
                            .flexGrow(1)
                            .styleModifier {
                                property("min-width", "0")
                                property("display", "-webkit-box")
                                property("-webkit-line-clamp", "2")
                                property("-webkit-box-orient", "vertical")
                                property("overflow", "hidden")
                                property("line-height", "1.35")
                            }
                            .toAttrs(),
                    ) {
                        SpanText(
                            text = data.title,
                            modifier = Modifier
                                .sFontSize(14)
                                .fontWeight(FontWeight.SemiBold)
                                .color(colors.textPrimary),
                        )
                        data.body?.takeIf { it.isNotBlank() }?.let { body ->
                            SpanText(
                                text = " $body",
                                modifier = Modifier
                                    .sFontSize(13)
                                    .color(colors.textSecondary),
                            )
                        }
                    }
                }
            }
        }
    }
}
